import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { buildEventsApiUrl } from '../config/api';
import { getAuthUserId } from '../utils/authUser';
import { buildAuthHeaders, buildJsonAuthHeaders } from '../utils/authFetch';

interface EventItem {
  id: string;
  name: string;
  createdByUserId?: string;
  addedByUserId?: string;
  userID?: string;
}

interface Preference {
  id: string;
  targetItemID: string;
  targetItemName: string;
  rank: number;
}

interface MathTradeStatusResponse {
  mathTrade?: {
    inputState?: 'collecting' | 'locked';
    tradeCutoffPassed?: boolean;
    publishedIsFinal?: boolean;
    publishedVisible?: boolean;
  };
}

interface BatchedPreferencesResponse {
  tradeLocked?: boolean;
  publishedIsFinal?: boolean;
  items?: EventItem[];
  preferencesByItem?: Record<string, Preference[]>;
}

const MyMathTradePreferencesPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const userId = getAuthUserId(user as any);

  const [loading, setLoading] = useState(true);
  const [savingItemId, setSavingItemId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<EventItem[]>([]);
  const [preferencesByItem, setPreferencesByItem] = useState<Record<string, Preference[]>>({});
  const [tradeLocked, setTradeLocked] = useState(false);
  const [publishedIsFinal, setPublishedIsFinal] = useState(false);

  const loadData = useCallback(async () => {
    if (!eventId || !userId) {
      setError('Missing event or user');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const authHeaders = buildAuthHeaders(userId);
      const [preferencesResponse, statusResponse] = await Promise.all([
        fetch(buildEventsApiUrl(`/api/events/${eventId}/math-trade/preferences?userId=${userId}`), { headers: authHeaders }),
        fetch(buildEventsApiUrl(`/api/events/${eventId}/math-trade/status`), { headers: authHeaders })
      ]);

      const preferencesData: BatchedPreferencesResponse & { error?: string } = await preferencesResponse.json();
      if (!preferencesResponse.ok) {
        throw new Error(preferencesData.error || 'Failed to load math trade preferences');
      }

      setItems(Array.isArray(preferencesData.items) ? preferencesData.items : []);
      setPreferencesByItem(preferencesData.preferencesByItem || {});
      setTradeLocked(Boolean(preferencesData.tradeLocked));
      setPublishedIsFinal(Boolean(preferencesData.publishedIsFinal));

      if (statusResponse.ok) {
        const statusData: MathTradeStatusResponse = await statusResponse.json();
        const mathTrade = statusData.mathTrade || {};
        const statusTradeLocked = Boolean(mathTrade.tradeCutoffPassed || mathTrade.inputState === 'locked');
        setTradeLocked(typeof preferencesData.tradeLocked === 'boolean' ? preferencesData.tradeLocked : statusTradeLocked);
        setPublishedIsFinal(typeof preferencesData.publishedIsFinal === 'boolean' ? preferencesData.publishedIsFinal : Boolean(mathTrade.publishedIsFinal));
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load math trade preferences');
    } finally {
      setLoading(false);
    }
  }, [eventId, userId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const movePreference = async (itemId: string, fromIndex: number, toIndex: number) => {
    const current = preferencesByItem[itemId] || [];
    if (tradeLocked) {
      setError('Want lists are locked because the trade cutoff has passed.');
      return;
    }
    if (toIndex < 0 || toIndex >= current.length || !eventId || !userId) return;

    const next = [...current];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);

    setPreferencesByItem(prev => ({ ...prev, [itemId]: next.map((pref, index) => ({ ...pref, rank: index + 1 })) }));
    setSavingItemId(itemId);

    try {
      const response = await fetch(buildEventsApiUrl(`/api/events/${eventId}/math-trade/items/${itemId}/preferences/order`), {
        method: 'PUT',
        headers: buildJsonAuthHeaders(userId),
        body: JSON.stringify({
          userId,
          orderedTargetItemIDs: next.map(pref => pref.targetItemID)
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to reorder preferences');
      }

      setPreferencesByItem(prev => ({
        ...prev,
        [itemId]: (data.preferences || []).map((pref: any) => ({
          id: pref.id,
          targetItemID: pref.targetItemID,
          targetItemName: pref.targetItemName || pref.targetItemID,
          rank: pref.rank
        }))
      }));
    } catch (err: any) {
      setError(err.message || 'Failed to reorder preferences');
      await loadData();
    } finally {
      setSavingItemId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/events/${eventId}`)} sx={{ mb: 3 }}>
        Back to Event
      </Button>

      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        My Want Lists
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Review and reorder the targets captured from your saved want-list selections.
      </Typography>

      {tradeLocked && (
        <Alert severity={publishedIsFinal ? 'success' : 'warning'} sx={{ mb: 3 }}>
          {publishedIsFinal
            ? 'Want lists are locked because final trade results have been published for this event.'
            : 'Want lists are locked because the trade cutoff has passed for this event.'}
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Stack spacing={3}>
        {items.map(item => {
          const preferences = preferencesByItem[item.id] || [];
          return (
            <Paper key={item.id} sx={{ p: 3, borderRadius: '12px' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                {item.name}
              </Typography>
              {preferences.length === 0 ? (
                <Alert severity="info">No ranked targets yet for this item.</Alert>
              ) : (
                <>
                  {preferences.length < 2 && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      This item only has one saved target right now, so there is nothing to reorder yet.
                    </Alert>
                  )}
                  <Stack spacing={1}>
                  {preferences.map((pref, index) => (
                    <Paper key={pref.id} variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          #{index + 1} — {pref.targetItemName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {pref.targetItemID}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1}>
                        <IconButton
                          size="small"
                          onClick={() => movePreference(item.id, index, index - 1)}
                          disabled={tradeLocked || index === 0 || savingItemId === item.id}
                        >
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => movePreference(item.id, index, index + 1)}
                          disabled={tradeLocked || index === preferences.length - 1 || savingItemId === item.id}
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
                </>
              )}
            </Paper>
          );
        })}

        {items.length === 0 && (
          <Alert severity="info">Add your items to this event first, then your want lists will show up here.</Alert>
        )}
      </Stack>
    </Container>
  );
};

export default MyMathTradePreferencesPage;
